import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShoppingCart, Check, Star, ArrowRight, CreditCard, Calendar, Zap, Package, Info } from 'lucide-react';
import PaymentChoiceModal from '../components/PaymentChoiceModal';
import { usePaymentChoice } from '../hooks/usePaymentChoice';

import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ActivitiesCommunityplace = () => {
  const { t: t } = useTranslation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [offers, setOffers] = useState({ subscription_plans: [], credit_packages: [] });
  const [loading, setLoading] = useState(true);
  const [offerLoading, setOfferLoading] = useState(false);
  const { paymentModalOpen, selectedPurchase, openPaymentModal, closePaymentModal } = usePaymentChoice();

  useEffect(() => {
    loadActivities();
  }, []);

  useEffect(() => {
    if (selectedActivity) {
      loadActivityOffers(selectedActivity.id);
    }
  }, [selectedActivity]);

  const loadActivities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/activities`);
      setActivities(response.data.activities || []);
      if (response.data.activities && response.data.activities.length > 0) {
        setSelectedActivity(response.data.activities[0]);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const loadActivityOffers = async (activityId) => {
    setOfferLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/activities/${activityId}/offers`);
      setOffers({
        subscription_plans: response.data.subscription_plans || [],
        credit_packages: response.data.credit_packages || []
      });
    } catch {
      setOffers({ subscription_plans: [], credit_packages: [] });
    } finally {
      setOfferLoading(false);
    }
  };

  const handleSubscribe = (plan) => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }
    // Navigate to subscription page with plan details
    navigate('/subscribe', { state: { activity: selectedActivity, plan } });
  };

  const handlePurchaseCredits = (package_) => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }
    // Ouvrir le modal de choix de paiement
    openPaymentModal({
      packageId: package_.id || 'credits_10',
      packageName: package_.name || `${package_.credits} Crédits`,
      amount: package_.price || package_.amount,
      description: `Pack de ${package_.credits} crédits pour ${selectedActivity?.name || 'vos services'}`
    });
  };

  const getActivityColorClass = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-green-600',
      emerald: 'from-emerald-500 to-emerald-600',
      amber: 'from-amber-500 to-amber-600',
      red: 'from-red-500 to-red-600'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des activités...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navigation />

      {/* Hero Section — branding Communityplace (Design O'SCOP Premium) */}
      <section
        className="relative text-white py-16 sm:py-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0a1e3f 0%,#0B3C5D 100%)' }}
        data-testid="communityplace-hero"
      >
        {/* Halos décoratifs */}
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-amber-400/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mb-8" />
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-5">
              <ShoppingCart className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-[10px] sm:text-xs tracking-[0.3em] text-amber-400 uppercase font-semibold mb-3" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              O'SCOP Outremer · Plateforme coopérative
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Communityplace
            </h1>
            <p className="text-base sm:text-lg text-slate-200 max-w-2xl mx-auto" style={{ fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}>
              Nos offres par activité : plans d'abonnement et packages de crédits, conçus pour les coopérateurs et leurs membres.
            </p>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mt-10" />
        </div>
      </section>

      {/* Activities Tabs */}
      <section className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto space-x-2 py-4">
            {activities.map((activity) => (
              <button
                key={activity.id}
                onClick={() => setSelectedActivity(activity)}
                className={`flex-shrink-0 px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedActivity?.id === activity.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-600 text-white hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{activity.icon}</span>
                {activity.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Activity Header */}
      {selectedActivity && (
        <section className={`bg-gradient-to-r ${getActivityColorClass(selectedActivity.color)} text-white py-12`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl">{selectedActivity.icon}</div>
              <div>
                <h2 className="text-3xl font-bold">{selectedActivity.name}</h2>
                <p className="text-lg opacity-90 mt-2">{selectedActivity.description}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Offers Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {offerLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des offres...</p>
            </div>
          ) : (
            <>
              {/* Subscription Plans */}
              {offers.subscription_plans.length > 0 && (
                <div className="mb-12">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      Plans d'Abonnement
                    </h3>
                    <p className="text-gray-600">
                      Accédez à des services illimités avec nos formules mensuelles ou annuelles
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {offers.subscription_plans.map((plan) => (
                      <Card 
                        key={plan.plan_id}
                        className={`relative overflow-hidden hover:shadow-2xl transition-all ${
                          plan.is_popular ? 'border-4 border-yellow-400 scale-105' : 'border-2'
                        }`}
                      >
                        {plan.is_popular && (
                          <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-lg font-bold text-sm">
                            <Star className="inline h-4 w-4 mr-1" />
                            POPULAIRE
                          </div>
                        )}
                        
                        <CardHeader className="bg-gradient-to-br from-gray-50 to-white">
                          <CardTitle className="text-2xl">{plan.name}</CardTitle>
                          <p className="text-gray-600 text-sm mt-2">{plan.description}</p>
                          <div className="mt-4">
                            <span className="text-4xl font-bold text-blue-600">{plan.price}€</span>
                            <span className="text-gray-600 ml-2">/ {plan.billing_period === 'monthly' ? 'mois' : 'an'}</span>
                          </div>
                        </CardHeader>

                        <CardContent className="pt-6">
                          <div className="space-y-3 mb-6">
                            {plan.features && plan.features.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>

                          <Button
                            onClick={() => handleSubscribe(plan)}
                            className="w-full bg-blue-600 hover:bg-blue-700 font-semibold py-3"
                          >
                            Souscrire
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Credit Packages */}
              {offers.credit_packages.length > 0 && (
                <div>
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      Packages de Crédits
                    </h3>
                    <p className="text-gray-600">
                      Achetez des crédits pour une utilisation flexible selon vos besoins
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {offers.credit_packages.map((pkg) => (
                      <Card 
                        key={pkg.package_id}
                        className="hover:shadow-xl transition-all border-2 hover:border-blue-300"
                      >
                        <CardHeader className="bg-gradient-to-br from-green-50 to-emerald-50 text-center">
                          <div className="text-4xl mb-3">
                            {pkg.credits_amount < 500 ? '📦' : pkg.credits_amount < 1000 ? '🎁' : '💎'}
                          </div>
                          <CardTitle className="text-xl">{pkg.name}</CardTitle>
                          <div className="mt-4">
                            <div className="text-3xl font-bold text-green-600">{pkg.price}€</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {pkg.credits_amount} crédits
                              {pkg.bonus_credits > 0 && (
                                <span className="text-green-600 font-semibold"> +{pkg.bonus_credits} bonus</span>
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="pt-6">
                          <div className="space-y-2 mb-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Zap className="h-4 w-4 text-yellow-500" />
                              <span>Utilisation flexible</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-blue-500" />
                              <span>Valable 1 an</span>
                            </div>
                            {pkg.bonus_credits > 0 && (
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 text-green-500" />
                                <span>+{pkg.bonus_credits} crédits offerts</span>
                              </div>
                            )}
                          </div>

                          <Button
                            onClick={() => handlePurchaseCredits(pkg)}
                            className="w-full bg-green-600 hover:bg-green-700 font-semibold"
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            Acheter
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* No offers message */}
              {offers.subscription_plans.length === 0 && offers.credit_packages.length === 0 && (
                <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed">
                  <CardContent className="p-12 text-center">
                    <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      Aucune offre disponible pour le moment
                    </h3>
                    <p className="text-gray-600">
                      Les offres pour cette activité seront bientôt disponibles.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />

      {/* Modal de choix de paiement */}
      {selectedPurchase && (
        <PaymentChoiceModal
          isOpen={paymentModalOpen}
          onClose={closePaymentModal}
          amount={selectedPurchase.amount}
          packageId={selectedPurchase.packageId}
          packageName={selectedPurchase.packageName}
          description={selectedPurchase.description}
        />
      )}
    </div>
  );
};

export default ActivitiesCommunityplace;
